// In-page documentation: what each component is, the algorithm it runs, what
// each engine call does step by step, and where all of it comes from in the
// literature. Kept as data so the same content drives both the docs overlay
// and the contextual "?" buttons next to each panel.

export interface DocSection {
  id: string;
  title: string;
  tagline: string;
  /** Prose paragraphs: what this is and why it has to exist. */
  body: string[];
  /** The algorithm, as the ordered steps the code actually performs. */
  algorithm?: { title: string; steps: string[] };
  /** Cost / complexity facts. */
  costs?: [string, string][];
  /** Where this lives in the codebase. */
  source?: string;
  /** Citation keys into REFERENCES. */
  refs?: string[];
}

export interface Reference {
  key: string;
  authors: string;
  title: string;
  /** Complete publication info including year — rendered verbatim. */
  venue: string;
  /** Used for the short inline chip label, e.g. "O'Neil 1996". */
  year: number;
  /** Why this specific paper matters for this specific code. */
  relevance: string;
}

export const REFERENCES: Reference[] = [
  {
    key: "oneil96",
    authors: "P. O'Neil, E. Cheng, D. Gawlick, E. O'Neil",
    title: "The Log-Structured Merge-Tree (LSM-Tree)",
    venue: "Acta Informatica 33(4), 351–385, 1996",
    year: 1996,
    relevance:
      "The original paper, and still the clearest statement of the core bargain: buffer writes in a memory component (C0), merge them into disk components (C1…Ck) in sorted batches, and pay in merge I/O what you save in random writes. Everything this engine does is a descendant of its rolling-merge argument. Read this first if you read only one.",
  },
  {
    key: "luo20",
    authors: "C. Luo, M. J. Carey",
    title: "LSM-based Storage Techniques: A Survey",
    venue: "The VLDB Journal 29, 393–418, 2020",
    year: 2020,
    relevance:
      "The best single entry point to the modern design space — compaction policies, partitioning, bloom allocation, and the amplification trade-offs, with a consistent vocabulary. If an interviewer asks 'what else could you have done here?', the alternatives are catalogued in this survey.",
  },
  {
    key: "pugh90",
    authors: "W. Pugh",
    title: "Skip Lists: A Probabilistic Alternative to Balanced Trees",
    venue: "Communications of the ACM 33(6), 668–676, 1990",
    year: 1990,
    relevance:
      "The memtable structure. Explains the coin-flip tower heights and why expected search is O(log n) without any rebalancing — which is exactly why it suits a structure that is written concurrently and then discarded wholesale.",
  },
  {
    key: "bloom70",
    authors: "B. H. Bloom",
    title: "Space/Time Trade-offs in Hash Coding with Allowable Errors",
    venue: "Communications of the ACM 13(7), 422–426, 1970",
    year: 1970,
    relevance:
      "The filter on every SSTable. Establishes the one-sided error property this engine depends on: a negative is definitive, so a 'no' lets a lookup skip a file without reading it.",
  },
  {
    key: "kirsch06",
    authors: "A. Kirsch, M. Mitzenmacher",
    title: "Less Hashing, Same Performance: Building a Better Bloom Filter",
    venue: "ESA 2006; Random Structures & Algorithms 33(2), 2008",
    year: 2006,
    relevance:
      "Justifies the double-hashing scheme in bloom.ts — deriving k probes as h₁ + i·h₂ from two hash functions, with no asymptotic penalty in false-positive rate versus k independent hashes.",
  },
  {
    key: "chang06",
    authors: "F. Chang, J. Dean, S. Ghemawat, et al.",
    title: "Bigtable: A Distributed Storage System for Structured Data",
    venue: "OSDI 2006",
    year: 2006,
    relevance:
      "Where the working vocabulary comes from — 'memtable', 'SSTable', minor vs. merging vs. major compaction — and the tablet-server recovery story that pairs a commit log with an in-memory buffer, the same shape as this engine's WAL plus memtable.",
  },
  {
    key: "mohan92",
    authors: "C. Mohan, D. Haderle, B. Lindsay, H. Pirahesh, P. Schwarz",
    title:
      "ARIES: A Transaction Recovery Method Supporting Fine-Granularity Locking and Partial Rollbacks Using Write-Ahead Logging",
    venue: "ACM TODS 17(1), 1992",
    year: 1992,
    relevance:
      "The write-ahead logging protocol itself: log before you act, and recover by replaying the log. This engine implements only the redo half (no in-place updates means no undo is needed), but the ordering discipline — durable log record before acknowledgement, truncate only after the data is durable elsewhere — is ARIES's.",
  },
  {
    key: "rosenblum92",
    authors: "M. Rosenblum, J. K. Ousterhout",
    title: "The Design and Implementation of a Log-Structured File System",
    venue: "ACM TOCS 10(1), 1992",
    year: 1992,
    relevance:
      "The ancestor idea: turn all writes into sequential appends and reclaim space with a background cleaner. LSM compaction is that cleaner, and the segment-cleaning cost analysis prefigures write amplification.",
  },
  {
    key: "lakshman10",
    authors: "A. Lakshman, P. Malik",
    title: "Cassandra: A Decentralized Structured Storage System",
    venue: "ACM SIGOPS Operating Systems Review 44(2), 2010",
    year: 2010,
    relevance:
      "Size-tiered compaction as deployed in practice — the strategy behind this engine's 'tiered' setting, where similarly sized runs accumulate and merge together rather than being folded into a sorted level.",
  },
  {
    key: "dayan17",
    authors: "N. Dayan, M. Athanassoulis, S. Idreos",
    title: "Monkey: Optimal Navigable Key-Value Store",
    venue: "SIGMOD 2017",
    year: 2017,
    relevance:
      "Shows that giving every level the same bits-per-key is wrong: false positives at deeper, larger levels cost more, so bloom memory should be skewed toward shallower ones. This engine uses a uniform allocation — Monkey is the principled improvement, and a good answer to 'how would you tune this further?'",
  },
  {
    key: "dayan18",
    authors: "N. Dayan, S. Idreos",
    title:
      "Dostoevsky: Better Space-Time Trade-Offs for LSM-Tree Based Key-Value Stores via Adaptive Removal of Superfluous Merging",
    venue: "SIGMOD 2018",
    year: 2018,
    relevance:
      "Formalises the leveled-versus-tiered trade-off this UI lets you toggle, and shows most merging effort at the largest level buys almost nothing — motivating hybrid 'lazy leveling'. The clearest treatment of why the amplification numbers move the way they do.",
  },
  {
    key: "dong17",
    authors: "S. Dong, M. Callaghan, L. Galanis, D. Borthakur, T. Savor, M. Strum",
    title: "Optimizing Space Amplification in RocksDB",
    venue: "CIDR 2017",
    year: 2017,
    relevance:
      "Production evidence that space amplification, not write amplification, is often the metric that actually matters — useful context for the third tile in the metrics panel.",
  },
  {
    key: "sears12",
    authors: "R. Sears, R. Ramakrishnan",
    title: "bLSM: A General Purpose Log Structured Merge Tree",
    venue: "SIGMOD 2012",
    year: 2012,
    relevance:
      "Attacks the latency spikes a naive scheduler produces when a large compaction blocks writes. This engine runs maintenance on a serialized background chain so writes never block; bLSM's spring-and-gear scheduler is the rigorous version.",
  },
];

export const SECTIONS: DocSection[] = [
  {
    id: "overview",
    title: "The one idea",
    tagline: "Never update in place. Buffer, append, merge later.",
    body: [
      "A B-tree updates data where it already lives: to change a key you find its page and rewrite it, which on a large dataset means a random write per update. An LSM tree refuses to do that. Writes accumulate in a sorted in-memory buffer; when the buffer is full it is written out as one immutable, sorted file; those files are merged in the background. Every disk write is sequential, and every file, once written, is read-only.",
      "Everything else in this engine is a consequence. Because a key is never overwritten, several versions of it can exist at once, so reads must search newest-to-oldest and stop at the first hit. Because obsolete versions accumulate, something must reclaim them, so there is compaction. Because a delete cannot erase a record inside an immutable file, deletion writes a tombstone instead. Because the in-memory buffer is lost in a crash, writes go to a log first.",
      "The cost of the bargain is that work is deferred, not avoided: you pay it later as write amplification (compaction rewriting data), read amplification (checking several files), and space amplification (obsolete versions still on disk). Tuning an LSM tree is choosing where on that three-way trade-off to sit — which is what the knobs in this page do.",
    ],
    costs: [
      ["write", "sequential appends only, no random in-place updates"],
      ["read", "may consult several files; bloom filters make most checks free"],
      ["space", "obsolete versions persist until a compaction reclaims them"],
    ],
    refs: ["oneil96", "luo20", "rosenblum92"],
  },
  {
    id: "write-path",
    title: "put(key, value) / delete(key)",
    tagline: "One log append, one in-memory insert, then acknowledge.",
    body: [
      "A delete is not a special operation — it is a write whose value is a tombstone marker. Both follow the identical path, which is why the trace for a delete looks like the trace for a put.",
      "Note what the write path does NOT do: it never reads an SSTable, never checks whether the key already exists, and never rewrites existing data. A put costs one sequential log append regardless of how much data is already stored. That constant cost, independent of dataset size, is the headline property of the design.",
    ],
    algorithm: {
      title: "Steps the engine executes, in order",
      steps: [
        "Assign the next sequence number. This is the global version ordering — every later comparison of 'which copy of this key is newer' is decided by it.",
        "Serialize the record and append it to the write-ahead log, then wait for the store to accept it. The write is not acknowledged before this completes.",
        "Insert into the skiplist memtable: O(log n) comparisons, in memory. If the key is already buffered, overwrite it in place — the memtable holds only the newest version of any key.",
        "Compare memtable size against the flush threshold. If it is full, freeze it, hand it to the background flusher, and start a fresh memtable so writes never block.",
      ],
    },
    costs: [
      ["durable I/O", "1 sequential append"],
      ["memory", "O(log n) expected comparisons"],
      ["reads performed", "none — a put never looks at existing data"],
    ],
    source: "src/engine/lsm.ts → write()",
    refs: ["oneil96", "mohan92"],
  },
  {
    id: "wal",
    title: "Write-ahead log",
    tagline: "The memtable is RAM. RAM dies. The log is the safety net.",
    body: [
      "Between a write being acknowledged and its memtable being flushed, the only durable copy of that write is its log record. The WAL exists to close exactly that window.",
      "Two orderings make it correct, and both are easy to get wrong. First, the log record must be durable before the write is acknowledged — otherwise you can acknowledge a write that a crash then loses. Second, log records may be truncated only after the flushed SSTable and the updated manifest are both durable — truncating earlier leaves a window where the data exists in neither place.",
      "Recovery is therefore trivial by construction: reload the manifest to learn which SSTables exist, replay the surviving log records into a fresh memtable in sequence order, and continue. There is no undo pass, because nothing was ever updated in place — a partially applied write cannot have corrupted existing data.",
    ],
    algorithm: {
      title: "Record layout (bytes, little-endian)",
      steps: [
        "u8 op — 0 for put, 1 for delete (tombstone)",
        "f64 seq — the sequence number, and also the log's ordering key",
        "u16 keyLen, then key bytes (UTF-8)",
        "u32 valLen, then value bytes (empty for a tombstone)",
      ],
    },
    source: "src/engine/wal.ts",
    refs: ["mohan92", "chang06"],
  },
  {
    id: "memtable",
    title: "Skiplist memtable",
    tagline: "Sorted in memory, so flushing needs no sort.",
    body: [
      "The memtable buffers recent writes and must be sorted, because flushing it must produce a sorted file in a single sequential pass. Any ordered structure would serve; this engine uses a skiplist, as LevelDB and RocksDB do.",
      "A skiplist is a linked list with express lanes. Each node is assigned a random tower height (here p = ¼, capped at 12 levels), and a search starts at the top lane, walks forward while the next key is smaller than the target, then drops a lane and repeats. Tall nodes are rare and skip far; short nodes are common and refine the position. Expected search cost is O(log n) with no rebalancing — nothing has to be rotated or split, which matters for a structure that is written hot and then thrown away whole.",
      "The trace panel reports the real comparison count for every memtable operation, so you can watch the log-shaped growth rather than take it on faith.",
    ],
    costs: [
      ["search / insert", "expected O(log₁/ₚ n) — with p = ¼, log base 4"],
      ["space", "expected 1/(1−p) ≈ 1.33 forward pointers per node"],
      ["flush", "O(n) single in-order walk, no sort needed"],
    ],
    source: "src/engine/skiplist.ts",
    refs: ["pugh90", "chang06"],
  },
  {
    id: "sstable",
    title: "SSTable format",
    tagline: "A sorted run of real bytes — index and filter travel with the data.",
    body: [
      "An SSTable is one immutable file: entries sorted by key, plus the metadata needed to search it without reading all of it. In this engine it is a genuine ArrayBuffer, and every lookup decodes those bytes. There is no in-memory map shadowing the file — if there were, the visualization would be measuring a lie.",
      "Immutability is what makes the rest of the system simple. A file that never changes needs no locking, can be cached without invalidation, and can be replaced atomically by a compaction that swaps manifest entries. Reclaiming space is a separate, batched concern rather than something reads and writes must coordinate over.",
    ],
    algorithm: {
      title: "Section layout, in write order",
      steps: [
        "Data section — repeated: u16 keyLen | key | u8 flags (bit 0 = tombstone) | f64 seq | u32 valLen | value.",
        "Sparse index — u32 count, then one (key, byte offset) pair for every 16th entry. Small enough to parse fully when the file is opened.",
        "Bloom filter — u8 hash count, u32 byte length, then the bit vector.",
        "Footer (12 bytes) — u32 index offset | u32 bloom offset | u32 magic 'LSM1'. Read the footer first to locate everything else; the magic detects corruption.",
      ],
    },
    source: "src/engine/sstable.ts",
    refs: ["chang06", "oneil96"],
  },
  {
    id: "bloom",
    title: "Bloom filter",
    tagline: "A cheap, one-sided 'definitely not here'.",
    body: [
      "Most lookups in an LSM tree are for keys that are not in most files. Without a filter, each of those wasted checks costs a real read. A bloom filter turns the common case into a handful of bit tests.",
      "It is a bit array with k hash functions. To add a key, set the k bits it hashes to. To test one, check those k bits: if any is zero the key was definitely never added; if all are set the key is probably present. The error is one-sided — false positives happen, false negatives cannot — and that asymmetry is what makes it safe to skip a file on a negative answer.",
      "This implementation derives its k probes from two hash functions as h₁ + i·h₂ rather than computing k independent hashes, following Kirsch and Mitzenmacher; h₂ is forced odd so the probe sequence covers the whole array.",
      "In the trace, 'bloom filter says NO' means the data section was never touched. 'Bloom said maybe, scan found nothing' is a false positive — the price paid for the filter's small size, and visible in the metrics panel as a wasted probe.",
    ],
    costs: [
      ["false-positive rate", "≈ 0.6185^(m/n) at the optimal k = (m/n)·ln2"],
      ["default here", "10 bits/key, 7 hashes → roughly 1% false positives"],
      ["test cost", "k bit lookups, no data read"],
    ],
    source: "src/engine/bloom.ts",
    refs: ["bloom70", "kirsch06", "dayan17"],
  },
  {
    id: "sparse-index",
    title: "Sparse index",
    tagline: "Index every 16th key; scan the gap.",
    body: [
      "A dense index — every key and its offset — would be large and mostly redundant, since a sorted file lets you find the neighbourhood cheaply and then walk. This engine indexes every 16th key with its byte offset.",
      "A lookup binary-searches the index for the greatest indexed key ≤ the target, which yields a byte offset, then decodes forward from there until the key is found or passed. Because entries are sorted, passing the target proves absence. The forward scan is bounded by the index interval, so it decodes at most 16 entries no matter how large the file is.",
      "The trade is the classic one: a wider interval shrinks the index and lengthens the scan. The trace shows both halves — the index comparison count and the entries actually decoded.",
    ],
    costs: [
      ["index search", "O(log(n/16)) comparisons"],
      ["forward scan", "≤ 16 entries decoded"],
      ["index size", "1/16th of the key count"],
    ],
    source: "src/engine/sstable.ts → SSTableReader.get()",
    refs: ["chang06", "oneil96"],
  },
  {
    id: "read-path",
    title: "get(key)",
    tagline: "Search newest to oldest; the first version found wins.",
    body: [
      "Because writes never update in place, a key may have versions in the memtable and in several SSTables simultaneously. The engine does not track where the newest one is — correctness comes from search order alone. Newer data is always shallower, so the first version encountered is by construction the newest, and the search stops there.",
      "If that first version is a tombstone, the key reads as deleted. Older live versions may still sit in deeper levels; they stay invisible because the search never reaches them.",
      "Within a level, only runs whose [minKey, maxKey] range covers the target are candidates — everything else is eliminated from the manifest without reading a byte. In L0 (and in tiered levels) runs overlap, so several may qualify and the highest sequence number among them wins. In leveled L1 and below, runs are non-overlapping, so at most one run per level can qualify.",
    ],
    algorithm: {
      title: "Steps the engine executes, in order",
      steps: [
        "Probe the active memtable. Hit → return immediately; this is the newest possible version.",
        "Probe any immutable memtables (frozen, flush still in flight), newest first.",
        "For each level, shallowest first: filter the level's runs by key range. A level with no covering run is skipped entirely.",
        "For each candidate run: test the bloom filter. A negative skips the file with no data read.",
        "On a bloom pass: binary-search the sparse index for a byte offset, then decode forward until the key is found or passed.",
        "Keep the highest sequence number found within the level, then return it — a tombstone returns 'not found'. If no level yields a version, the key does not exist.",
      ],
    },
    costs: [
      ["leveled", "≤ 1 candidate run per level; bloom filters cut most to zero reads"],
      ["tiered", "up to T candidate runs per level — the cost of cheaper writes"],
      ["best case", "memtable hit: zero SSTables touched"],
    ],
    source: "src/engine/lsm.ts → get()",
    refs: ["oneil96", "dayan17", "luo20"],
  },
  {
    id: "compaction",
    title: "Compaction",
    tagline: "The background merge that pays the deferred bill.",
    body: [
      "Flushing alone would accumulate files without bound: reads would consult more and more of them, and obsolete versions would never be reclaimed. Compaction merges overlapping runs, keeps only the newest version of each key, writes the result one level deeper, and atomically swaps the inputs for the outputs in the manifest.",
      "The merge itself is a k-way merge over sorted inputs — the same algorithm as merging sorted lists. Every input is already in key order, so one linear pass produces sorted output with no sorting step anywhere. This is why the memtable had to be sorted: sortedness, established once in memory, is preserved by every subsequent operation.",
      "Both strategies here run that identical merge; they differ only in which runs get picked, which is the honest way to present the choice.",
      "Leveled (RocksDB-style): each level has a byte budget growing by the fan-out factor. When a level exceeds it, one run is pushed down and merged with every run it overlaps in the level below, keeping that level non-overlapping. Reads stay cheap (one run per level) but data is rewritten repeatedly on its way down — higher write amplification.",
      "Size-tiered (Cassandra-style): runs accumulate at a level until enough have piled up, then all of them merge into one run a level down. Each entry is written about once per level, so writes are cheaper, but a level holds many overlapping runs, so reads must check more of them — and until the merge happens, several copies of a key occupy space.",
    ],
    algorithm: {
      title: "Steps the engine executes, in order",
      steps: [
        "Pick inputs by policy: leveled picks the oldest run from the first level over budget; tiered picks a whole tier once it holds enough runs.",
        "Compute the merged key range and pull in every run in the target level that overlaps it — those must be rewritten to keep the target non-overlapping.",
        "Open a cursor per input run and k-way merge: repeatedly take the smallest key across cursors, and among duplicates of that key keep the highest sequence number. Older versions are discarded here — this is where space is reclaimed.",
        "Discard tombstones only if this merge writes the bottom level; otherwise carry them forward.",
        "Cut output into runs at the target size, writing each as a complete SSTable with its own index and filter.",
        "Swap inputs for outputs in the manifest, persist it, then delete the input files. The manifest write is the atomic commit point.",
        "Re-check triggers: one compaction can push the next level over budget, cascading downward.",
      ],
    },
    source: "src/engine/lsm.ts → compact()",
    refs: ["oneil96", "lakshman10", "dayan18", "sears12", "luo20"],
  },
  {
    id: "tombstones",
    title: "Deletes and tombstones",
    tagline: "A delete is a write, and it cannot retire early.",
    body: [
      "SSTables are immutable, so a delete cannot remove anything. It writes a tombstone: a marker that shadows every older version of that key during a read.",
      "The subtle rule is when a tombstone may finally be discarded. Dropping one while any older version of that key still exists in a deeper level would make the deleted value visible again — a resurrection bug, and a genuinely nasty one because it appears only after a particular compaction ordering. This engine therefore discards tombstones only in a compaction that writes the bottom level, where by definition nothing older survives below.",
      "The verification suite exercised exactly this: 50 deleted keys re-checked after every compaction, with the tombstones confirmed to reach and drop at the bottom level rather than the check passing vacuously.",
    ],
    source: "src/engine/lsm.ts → compact(), isBottom",
    refs: ["luo20", "lakshman10"],
  },
  {
    id: "scan",
    title: "scan(lo, hi)",
    tagline: "k-way merge across every component at once.",
    body: [
      "A range scan cannot stop at the first hit the way a point lookup does — it must produce every live key in the range, in order, from components that each hold part of the answer.",
      "The engine opens a cursor on the memtable and on every run whose range intersects [lo, hi), then repeatedly emits the smallest key across all cursors. Duplicates of that key are drained together and the highest sequence number wins; if that winner is a tombstone the key is omitted. Because every component is sorted, this is one linear merge with no buffering of the whole range.",
      "This is also how space amplification is measured honestly in the metrics panel: a real full scan through this path, not a bookkeeping counter that could drift from reality.",
    ],
    source: "src/engine/lsm.ts → scan()",
    refs: ["oneil96", "luo20"],
  },
  {
    id: "recovery",
    title: "Crash recovery",
    tagline: "Reload the manifest, replay the log, continue.",
    body: [
      "Refreshing this page is a real crash: the memtable and every in-flight flush vanish, while IndexedDB keeps the WAL records, the SSTable bytes, and the manifest. 'Crash & recover' does the same thing without the refresh.",
      "Recovery reads the manifest to learn which SSTables exist and at which levels, replays the surviving WAL records into a fresh memtable in sequence order, and resumes. Anything acknowledged is either already in an SSTable or still in the log, so nothing acknowledged is lost — which is what the verification suite checked across three crash cycles.",
      "There is no undo phase. Because data is never modified in place, a crash mid-flush leaves an unreferenced file rather than a corrupted one, and the manifest simply never points at it.",
    ],
    source: "src/engine/lsm.ts → open(), crashAndRecover()",
    refs: ["mohan92", "chang06"],
  },
  {
    id: "amplification",
    title: "The three amplifications",
    tagline: "Every knob trades one against the others.",
    body: [
      "These three numbers are the vocabulary of storage-engine tuning, and no configuration wins on all of them at once. Watching them move as you turn the knobs is the point of the metrics panel.",
      "Write amplification — bytes physically written divided by bytes the client logically wrote. Above 1 because compaction rewrites data repeatedly as it descends. Leveled rewrites more than tiered.",
      "Read amplification — how many runs a lookup must consult. Leveled keeps this near one run per level; tiered can require checking every run in a tier. Bloom filters attack the constant, not the asymptotics: the tables are still consulted, but most cost only bit tests.",
      "Space amplification — bytes on disk divided by live logical bytes. Obsolete versions and undropped tombstones occupy space until a compaction reclaims them, so tiered (which lets duplicates accumulate longer) tends to be worse.",
      "Roughly, with size ratio T and L levels: leveled costs about T·L write amplification with ~1 run per level on reads; tiered costs about L write amplification with up to T runs per level on reads and up to T× space. Dostoevsky derives these carefully and shows the hybrid points in between.",
    ],
    costs: [
      ["leveled", "WA ≈ T·L · RA ≈ L runs · SA bounded"],
      ["tiered", "WA ≈ L · RA ≈ T·L runs · SA up to T"],
      ["measured here", "seed 2026: leveled WA 7.62× / tiered WA 6.00×"],
    ],
    refs: ["dayan18", "dayan17", "dong17", "luo20"],
  },
];

export function sectionById(id: string): DocSection | undefined {
  return SECTIONS.find((s) => s.id === id);
}

export function refByKey(key: string): Reference | undefined {
  return REFERENCES.find((r) => r.key === key);
}
